import type { MutationFunction } from "@tanstack/react-query";
import { Flex, Link, List, Text } from "@wayflyer/flyui";
import { useIntl } from "react-intl";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import type {
  FormattedFundingApplicationScreen,
  FormattedFundingApplicationScreenUpdate,
} from "../../../queries/funding-application/queries";
import { UploadDocumentType } from "../../../types";
import { useStageOptOutCheckbox } from "../hooks";

import { DocumentUpload } from "./document-upload";
import { MESSAGES } from "./message-definitions";
import { ReportingCurrencySelect } from "./reporting-currency-input";

export const BalanceSheetDocumentUpload = ({
  fundingApplication,
  accountingThreshold,
  mutate,
  hasUploadedDocuments,
  isVerificationPage,
  error,
}: {
  fundingApplication: FormattedFundingApplicationScreen;
  accountingThreshold: Record<string, number>;
  mutate: MutationFunction<FormattedFundingApplicationScreenUpdate>;
  hasUploadedDocuments: boolean;
  isVerificationPage?: boolean;
  error?: string;
}) => {
  const intl = useIntl();
  const { canOptOutOfStage } = useStageOptOutCheckbox(UploadDocumentType.BALANCE_SHEET);

  const REQUIRED_DURATION = 24;
  const exampleDocHref = "/static/example-docs/balance-sheet-12m-YTD.pdf";

  const capAmountCurrency = fundingApplication.requestedAmountCurrency ?? "EUR";
  const capAmount = accountingThreshold[capAmountCurrency].toLocaleString();

  const shouldShowOptOutCheckbox = !!canOptOutOfStage;

  const handleAccountingOptOut = (checked: boolean) =>
    mutate({ id: fundingApplication.id, accountingOptedOut: checked });

  return (
    <DocumentUpload
      documentType={UploadDocumentType.BALANCE_SHEET}
      supportedFileExtensions={[".xlsx", ".pdf"]}
      fileSizeLimitMB={50}
      disabled={fundingApplication.accountingOptedOut}
      slot={
        <Flex maxWidth="400px">
          <ReportingCurrencySelect
            initialValue={fundingApplication.accountingCurrency ?? undefined}
            disabled={fundingApplication.accountingOptedOut}
            error={
              hasUploadedDocuments && !fundingApplication.accountingCurrency && !fundingApplication.accountingOptedOut
                ? intl.formatMessage(COMMON_MESSAGES.pleaseFillOutThisField)
                : undefined
            }
            onChange={(item) => mutate({ id: fundingApplication.id, accountingCurrency: item.text })}
            helperText={intl.formatMessage(MESSAGES.reportingCurrencyHelperText)}
          />
        </Flex>
      }
      fieldLabel={intl.formatMessage(
        isVerificationPage ? MESSAGES.updateUploadsFieldLabel : MESSAGES.uploadDocumentsFieldLabel
      )}
      description={
        isVerificationPage
          ? ""
          : intl.formatMessage(MESSAGES.balanceSheetDescription, {
              ul: (chunks: string) => <List type="bullet">{chunks}</List>,
              li: (chunks: string) => <List.Item>{chunks}</List.Item>,
              p: (chunks: string) => <Text>{chunks}</Text>,
              a: (chunks: string) => (
                <Link url={exampleDocHref} external>
                  {chunks}
                </Link>
              ),
              duration: REQUIRED_DURATION,
            })
      }
      onSkipCheckbox={shouldShowOptOutCheckbox ? handleAccountingOptOut : undefined}
      skipped={fundingApplication.accountingOptedOut}
      skipCheckboxDescription={intl.formatMessage(MESSAGES.accountingSkipCheckboxLabel, {
        capAmount: `${capAmount} ${capAmountCurrency}`,
      })}
      error={error}
    />
  );
};
